import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cityPriceFaq } from "./cityPricing";

const gassin: ChauffeurPage = {
  slug: "gassin",
  title: "Chauffeur privé à Gassin – VTC village perché",
  subtitle:
    "Chauffeur privé et VTC à Gassin : transfert aéroport de Nice, gare de Saint-Raphaël, plages de Pampelonne, mise à disposition et liaison Saint-Tropez.",
  keywords: [
    "chauffeur privé gassin",
    "vtc gassin",
    "chauffeur gassin",
    "transfert gassin aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Gassin",
    subtitle:
      "L’un des Plus Beaux Villages de France, au-dessus de Pampelonne : transfert aéroport de Nice, mise à disposition et longue distance, 7j/7.",
    keywords: [
      "VTC Gassin",
      "chauffeur plages de Pampelonne",
      "transfert aéroport Nice",
      "chauffeur golfe de Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Gassin (golfe de Saint-Tropez)",
    badges: [
      "Village perché & Pampelonne",
      "Transfert aéroport de Nice",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Gassin",
    items: [
      {
        title: "Village perché maîtrisé",
        description:
          "Ruelles étroites (dont l’Androuno) et stationnement limité en haut du village : le chauffeur vous dépose au plus près et revient à la demande.",
      },
      {
        title: "Au-dessus de Pampelonne",
        description:
          "Position dominante entre le village et les plages de Pampelonne : navettes plage sans souci de parking.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l’aéroport de Nice, environ 1h20 à 1h30 de route, gérées dans un confort haut de gamme.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Gassin",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Gassin ⇄ aéroport de Nice Côte d’Azur avec suivi de vol ; trajet plus long qu’au départ de Cannes, calé dès la prise en charge.",
      },
      {
        title: "Gare TGV de Saint-Raphaël",
        text: "Prise en charge à la gare de Saint-Raphaël, à environ 30 à 40 minutes, comme alternative au train.",
      },
      {
        title: "Navettes plages de Pampelonne",
        text: "Vers les clubs de plage de Pampelonne (Ramatuelle voisine) et retour, sans contrainte de stationnement.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée pour le village, les domaines viticoles, les plages et Saint-Tropez.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Gassin : Cannes, Nice, Monaco et au-delà, avec un chauffeur dédié.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Village de Gassin",
        description:
          "Village perché classé Plus Beaux Villages de France, ruelles et panorama.",
      },
      {
        name: "L’Androuno",
        description:
          "L’une des plus étroites ruelles de France, au cœur du village.",
      },
      {
        name: "Plages de Pampelonne",
        description: "Clubs de plage en contrebas, sur Ramatuelle voisine.",
      },
      {
        name: "Domaines viticoles",
        description: "Vignobles des coteaux du golfe, autour du village.",
      },
      {
        name: "Gare de Saint-Raphaël",
        description: "Gare TGV à environ 30-40 min, point de transfert.",
      },
      {
        name: "Aéroport de Nice",
        description: "Aéroport Nice Côte d’Azur, à environ 1h20-1h30.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour Gassin",
    subheading:
      "Du village perché aux plages de Pampelonne, un service premium dans le golfe de Saint-Tropez.",
    paragraphs: [
      "Gassin, classé parmi les Plus Beaux Villages de France, coiffe un piton au-dessus de Pampelonne : ruelles étroites — dont la fameuse Androuno —, panorama sur le golfe et les vignobles, stationnement rare en haut du village. Un chauffeur qui dépose au plus près de l’entrée piétonne évite la recherche d’une place et les manœuvres dans les venelles.",
      "Le village n’a pas d’aéroport : l’arrivée se fait le plus souvent par Nice Côte d’Azur, à environ 1h20 à 1h30 de route selon le trafic estival. Nous calons l’heure de départ pour limiter l’attente, et proposons la gare TGV de Saint-Raphaël, à une trentaine de minutes, comme point de transfert alternatif.",
      "En contrebas, les plages de Pampelonne attirent tout l’été : un même chauffeur assure les navettes plage, les dîners au village, les domaines viticoles et Saint-Tropez tout proche, avec discrétion et horaires souples. Forfaits fixes ou mise à disposition à l’heure : devis immédiat.",
    ],
    bullets: [
      "Dépose au plus près du village",
      "Navettes plages de Pampelonne",
      "Transfert aéroport de Nice & gare de Saint-Raphaël",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Gassin",
    steps: [
      "Indiquez votre trajet (village, Pampelonne, aéroport, gare) et vos horaires.",
      "Nous recommandons le véhicule et le forfait adaptés.",
      "Le chauffeur suit votre vol ou votre train et ajuste le départ.",
      "Transfert direct ou mise à disposition pour le golfe pendant votre séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Gassin"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Gassin ?",
      a: "Comptez environ 1h20 à 1h30 hors affluence, davantage lors des week-ends et pics de trafic estivaux du golfe. Nous adaptons l’heure de départ et suivons votre vol.",
    },
    {
      q: "Desservez-vous la gare TGV de Saint-Raphaël ?",
      a: "Oui, la gare de Saint-Raphaël se situe à environ 30 à 40 minutes de Gassin ; c’est un point de transfert alternatif pratique pour les arrivées et départs en train.",
    },
    {
      q: "Assurez-vous la liaison avec Saint-Tropez ?",
      a: "Oui, Saint-Tropez est tout proche ; nous assurons les allers-retours vers le port, les restaurants et les soirées, avec retour de nuit.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En haute saison estivale, réservez au plus tôt : la demande est forte dans le golfe de Saint-Tropez. Nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale et aux séjours en villa.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes, van premium (Classe V), SUV et minibus selon le nombre de passagers et les bagages, adaptés à la longue distance depuis Nice ou Cannes.",
    },
    {
      q: "Assurez-vous les navettes vers les plages de Pampelonne ?",
      a: "Oui, depuis Gassin nous desservons les clubs de plage de Pampelonne (sur Ramatuelle voisine) en aller-retour, sans contrainte de stationnement et avec des horaires souples l’été.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur privé à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    { label: "Chauffeur Cannes, tarifs et trajets", href: "/chauffeur/cannes" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location avec livraison à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Gassin",
    text: "Transfert aéroport de Nice, gare de Saint-Raphaël ou navette vers Pampelonne : réservez votre chauffeur, réponse rapide 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Gassin — VTC & transfert aéroport",
    description:
      "Chauffeur privé et VTC à Gassin : transfert aéroport de Nice, gare de Saint-Raphaël, navettes plages de Pampelonne, mise à disposition et liaison Saint-Tropez.",
    canonical: "/chauffeur/gassin",
  },
};

export default gassin;
